import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BillingAdminService } from './Service';
import { CircleCheck, Hourglass, Lock, RotateCw, SlidersHorizontal, Store, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const service = new BillingAdminService();

const PLAN_BADGE_CLASS: Record<string, string> = {
    trial: 'badge-warning',
    basic: 'badge-info',
    professional: 'badge-primary',
    premium: 'badge-secondary',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
    active: 'badge-success',
    trial: 'badge-warning',
    blocked: 'badge-error',
    unknown: 'badge-ghost',
};

const ATTENTION_CLASS: Record<string, string> = {
    high: 'border-error/30 bg-error/10 text-error',
    medium: 'border-warning/30 bg-warning/10 text-warning',
    low: 'border-info/30 bg-info/10 text-info',
};

const statusLabel = (status?: string) => {
    if (status === 'active') return 'Ativo';
    if (status === 'trial') return 'Trial';
    if (status === 'blocked') return 'Bloqueado';
    return 'Indefinido';
};

const subscriptionStatusLabel = (status?: string | null) => {
    if (status === 'active') return 'Ativa';
    if (status === 'pending') return 'Pendente';
    if (status === 'past_due') return 'Em atraso';
    if (status === 'blocked') return 'Bloqueada';
    if (status === 'cancelled') return 'Cancelada';
    return 'Sem assinatura';
};

const formatDate = (value?: string | Date | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string | Date | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR');
};

const trialLabel = (row: any) => {
    if (row?.billingStatus !== 'trial') return '-';
    if (row?.trialDaysRemaining === null || row?.trialDaysRemaining === undefined) {
        return 'Sem data final';
    }
    if (row.trialDaysRemaining <= 0) return 'Expirado';
    if (row.trialDaysRemaining === 1) return '1 dia';
    return `${row.trialDaysRemaining} dias`;
};

function MetricCard({
    label,
    value,
    icon: Icon,
    tone,
}: {
    label: string;
    value: number | string;
    icon: LucideIcon;
    tone: string;
}) {
    return (
        <div className={`sf-kpi-card ${tone} rounded-2xl`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="sf-kpi-pill text-xs uppercase">{label}</div>
                    <div className="sf-kpi-value mt-2 text-2xl font-semibold text-base-content">{value}</div>
                </div>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-base-100/60">
                    <Icon className="text-base" aria-hidden="true" />
                </span>
            </div>
        </div>
    );
}

export default function BillingOverview() {
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await service.getOverview();
            setData(response.data);
        } catch (err: any) {
            console.error('Erro ao carregar painel de cobranca SaaS', err);
            setError(err?.response?.data?.message || 'Nao foi possivel carregar o painel de cobranca.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const rows = data?.establishments || [];
    const attention = data?.attention || [];
    const summary = data?.summary || {};

    const planDistribution = useMemo(() => data?.planDistribution || [], [data]);

    if (loading && !data) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body">Carregando painel de cobranca...</div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body gap-4">
                    <div className="alert alert-error">{error}</div>
                    <button type="button" className="btn btn-primary w-fit" onClick={load}>
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 pb-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <MetricCard
                    label="Estabelecimentos"
                    value={summary.total || 0}
                    icon={Store}
                    tone="sf-tone-primary"
                />
                <MetricCard
                    label="Ativos"
                    value={summary.active || 0}
                    icon={CircleCheck}
                    tone="sf-tone-success"
                />
                <MetricCard
                    label="Trial"
                    value={summary.trial || 0}
                    icon={Hourglass}
                    tone="sf-tone-warning"
                />
                <MetricCard
                    label="Bloqueados"
                    value={summary.blocked || 0}
                    icon={Lock}
                    tone="sf-tone-secondary"
                />
                <MetricCard
                    label="Atenção"
                    value={summary.attention || 0}
                    icon={TriangleAlert}
                    tone="sf-tone-info"
                />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <div className="card sf-dashboard-panel xl:col-span-2">
                    <div className="card-body gap-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Status dos planos</h3>
                                <p className="text-sm text-base-content/60">
                                    Distribuicao comercial atual dos estabelecimentos.
                                </p>
                            </div>
                            <button type="button" className="btn btn-outline btn-sm" onClick={load}>
                                <RotateCw aria-hidden="true" />
                                Atualizar
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {planDistribution.map((plan: any) => {
                                const total = Number(summary.total || 0);
                                const percent = total > 0 ? Math.round((plan.total / total) * 100) : 0;

                                return (
                                    <div
                                        key={plan.planKey}
                                        className="rounded-2xl border border-base-200 bg-base-100/70 p-4"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className={`badge ${PLAN_BADGE_CLASS[plan.planKey] || 'badge-ghost'}`}>
                                                {plan.planLabel}
                                            </span>
                                            <span className="text-sm font-semibold">{plan.total}</span>
                                        </div>
                                        <div className="mt-3 h-2 overflow-hidden rounded-full bg-base-200">
                                            <div
                                                className="h-full rounded-full bg-primary"
                                                style={{ width: `${percent}%` }}
                                            />
                                        </div>
                                        <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-base-content/65">
                                            <span>Ativos: {plan.active || 0}</span>
                                            <span>Trial: {plan.trial || 0}</span>
                                            <span>Bloq.: {plan.blocked || 0}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="card sf-dashboard-panel">
                    <div className="card-body gap-4">
                        <div>
                            <h3 className="text-lg font-semibold">Asaas</h3>
                            <p className="text-sm text-base-content/60">Estado tecnico da integracao.</p>
                        </div>

                        <div className="space-y-3 text-sm">
                            <div className="flex items-center justify-between rounded-2xl border border-base-200 bg-base-100/70 px-4 py-3">
                                <span>Ambiente</span>
                                <span className="font-semibold">{data?.gateway?.environment || 'sandbox'}</span>
                            </div>
                            <div className="flex items-center justify-between rounded-2xl border border-base-200 bg-base-100/70 px-4 py-3">
                                <span>API key</span>
                                <span className={`badge ${data?.gateway?.configured ? 'badge-success' : 'badge-warning'}`}>
                                    {data?.gateway?.configured ? 'Configurada' : 'Pendente'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between rounded-2xl border border-base-200 bg-base-100/70 px-4 py-3">
                                <span>Assinaturas locais</span>
                                <span className="font-semibold">{summary.subscriptions || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Atenção operacional</h3>
                        <div className="mt-3 space-y-3">
                            {attention.length === 0 && (
                                <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4 text-sm text-base-content/60">
                                    Nenhum estabelecimento exige acao agora.
                                </div>
                            )}
                            {attention.slice(0, 8).map((row: any) => (
                                <div
                                    key={row.id}
                                    className={`rounded-2xl border p-4 ${ATTENTION_CLASS[row.attention?.severity || 'low']}`}
                                >
                                    <div className="font-semibold text-base-content">{row.name}</div>
                                    <div className="mt-1 text-sm text-base-content/75">{row.attention?.reason}</div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <span className={`badge ${STATUS_BADGE_CLASS[row.billingStatus] || 'badge-ghost'}`}>
                                            {statusLabel(row.billingStatus)}
                                        </span>
                                        <span className={`badge ${PLAN_BADGE_CLASS[row.planKey] || 'badge-ghost'}`}>
                                            {row.planLabel}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card sf-dashboard-panel xl:col-span-2">
                    <div className="card-body gap-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Estabelecimentos e cobrança</h3>
                                <p className="text-sm text-base-content/60">
                                    Acompanhamento root ate a assinatura Asaas entrar em producao.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate('/estabelecimentos')}
                            >
                                <SlidersHorizontal aria-hidden="true" />
                                Gerenciar
                            </button>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>Estabelecimento</th>
                                        <th>Plano</th>
                                        <th>Cobranca</th>
                                        <th>Asaas</th>
                                        <th>Trial</th>
                                        <th>Ativacao</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row: any) => (
                                        <tr key={row.id}>
                                            <td>
                                                <div className="font-medium">{row.name}</div>
                                                {row.legacyFallback && (
                                                    <div className="text-xs text-base-content/55">
                                                        Fallback legado aplicado
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${PLAN_BADGE_CLASS[row.planKey] || 'badge-ghost'}`}>
                                                    {row.planLabel}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${STATUS_BADGE_CLASS[row.billingStatus] || 'badge-ghost'}`}>
                                                    {statusLabel(row.billingStatus)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="font-medium">
                                                    {subscriptionStatusLabel(row.gateway?.subscriptionStatus)}
                                                </div>
                                                <div className="text-xs text-base-content/55">
                                                    Venc.: {formatDate(row.gateway?.nextDueDate)}
                                                </div>
                                            </td>
                                            <td>
                                                <div>{trialLabel(row)}</div>
                                                <div className="text-xs text-base-content/55">
                                                    Ate {formatDate(row.trialEndsAt)}
                                                </div>
                                            </td>
                                            <td>{formatDate(row.billingActivatedAt)}</td>
                                        </tr>
                                    ))}
                                    {rows.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="py-8 text-center text-base-content/60">
                                                Nenhum estabelecimento encontrado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="text-xs text-base-content/50">
                            Ultima atualizacao: {formatDateTime(data?.generatedAt)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
